import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import {
  getDigestAccountUserId,
  isDigestEligible,
  teamMemberMustSelectArtistFirst,
} from './lib/digestEligibility'
import { lazy, Suspense, useEffect } from 'react'
import { ThemeProvider } from './context/ThemeContext'
import { initAccessibility } from './hooks/useAccessibility'
import { setGA4UserId, setGA4UserProperties } from './lib/analytics'
import Layout from './components/Layout'
import RequireAuth from './components/RequireAuth'
import RequireProtect from './components/RequireProtect'
import ProtectedRoute from './components/ProtectedRoute'
import PageTransition from './components/PageTransition'
import PageSkeleton from './components/PageSkeleton'
import RouteTracker from './components/RouteTracker'
import ErrorBoundary from './components/ErrorBoundary'
import HomePage from './pages/HomePage'
import TestPage from './pages/TestPage'
import LoginPage from './pages/LoginPage'
import LoginOtpPage from './pages/LoginOtpPage'
import SignupPage from './pages/signup/SignupPage'
import ProtectPage from './pages/ProtectPage'
import DeckPage from './pages/DeckPage'
import Deck2Page from './pages/Deck2Page'
import Deck3Page from './pages/Deck3Page'
import DeckFinalPage from './pages/DeckFinalPage'
import Deck4Page from './pages/Deck4Page'
import ProductDeckPage from './pages/ProductDeckPage'
import SafeDeckPage from './pages/SafeDeckPage'
import SyncfulDeckPage from './pages/SyncfulDeckPage'
import TeamHandbookPage from './pages/TeamHandbookPage'
import MasterPlanPage from './pages/MasterPlanPage'
import PlanRichardPage from './pages/PlanRichardPage'
import PlanRolloutPage from './pages/PlanRolloutPage'
import PlanAprilPage from './pages/PlanAprilPage'
import ActionPlanPage from './pages/ActionPlanPage'
import AgendaPage from './pages/AgendaPage'
import StrategicAdvisoryGroupPage from './pages/StrategicAdvisoryGroupPage'
import MultiIndustryDeckPage from './pages/MultiIndustryDeckPage'
import CrossReferenceDataDeckPage from './pages/CrossReferenceDataDeckPage'
import AdminPage from './pages/admin/AdminPage'
import DeveloperPortalPage from './pages/DeveloperPortalPage'
import ApiDocsPage from './pages/ApiDocsPage'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import TermsOfServicePage from './pages/legal/TermsOfServicePage'
import AccessibilityStatementPage from './pages/legal/AccessibilityStatementPage'
import WhitepaperPage from './pages/WhitepaperPage'
import WrappedPage from './pages/WrappedPage'
import DashboardOverviewPage from './pages/DashboardOverviewPage'
import LinksManageRedirect from './components/links/LinksManageRedirect'

// ── Lazy-loaded pages ──────────────────────────────────────────────
// Each page is loaded on-demand so sidebar navigation is instant.
// The <Suspense> boundary shows a lightweight skeleton while the
// chunk downloads / parses.
const ShowsPage = lazy(() => import('./pages/ShowsPage'))
const SchedulePage = lazy(() => import('./pages/SchedulePage'))
const TeamPage = lazy(() => import('./pages/TeamPage'))
const TeamMemberEditPage = lazy(() => import('./pages/TeamMemberEditPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const GoogleCalendarCallbackPage = lazy(() => import('./pages/GoogleCalendarCallbackPage'))
const KnowledgePage = lazy(() => import('./pages/KnowledgePage'))
const MusicPage = lazy(() => import('./pages/MusicPage'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const TasksPage = lazy(() => import('./pages/TasksPage'))
const LinksPage = lazy(() => import('./pages/LinksPage'))
const CollectLinkPage = lazy(() => import('./pages/CollectLinkPage'))
const ReleaseLinkPage = lazy(() => import('./pages/ReleaseLinkPage'))
const GuestListPage = lazy(() => import('./pages/GuestListPage'))
const TicketsPage = lazy(() => import('./pages/TicketsPage'))
const PeoplePage = lazy(() => import('./pages/PeoplePage'))
const OverviewPage = lazy(() => import('./pages/OverviewPage'))
const ActivityPage = lazy(() => import('./pages/ActivityPage'))
const MoneyPage = lazy(() => import('./pages/MoneyPage'))
const MoneyTestPage = lazy(() => import('./pages/MoneyTestPage'))
const DealsPage = lazy(() => import('./pages/DealsPage'))
const DealDetailPage = lazy(() => import('./pages/DealDetailPage'))
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'))
const DigestPage = lazy(() => import('./pages/DigestPage'))
const TourManagerPage = lazy(() => import('./pages/TourManagerPage'))
const AgentSetupPage = lazy(() => import('./pages/AgentSetupPage'))
const AgentChatPage = lazy(() => import('./pages/AgentChatPage'))
const EmailPage = lazy(() => import('./pages/EmailPage'))
const IntelligenceDashboardPage = lazy(() => import('./pages/IntelligenceDashboardPage'))

/**
 * Redirect team members (manager/support/promoter) with no active artists to Team.
 * Otherwise go to /dashboard.
 */
function SmartRedirect() {
  if (teamMemberMustSelectArtistFirst()) {
    return <Navigate to="/team" replace />
  }
  const accountId = getDigestAccountUserId()
  if (accountId && isDigestEligible(accountId)) {
    return <Navigate to="/digest" replace />
  }
  return <Navigate to="/dashboard" replace />
}

function App() {
  useEffect(() => {
    const cleanup = initAccessibility()
    return cleanup
  }, [])

  useEffect(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      if (!raw) return
      const user = JSON.parse(raw)
      if (user?._id) setGA4UserId(user._id)
      if (user?.professionRole || user?.plan) {
        setGA4UserProperties({
          user_role: user.professionRole || 'artist',
          user_plan: user.plan || 'basic',
          has_team: user.selectedArtists?.length > 0 ? 'true' : 'false',
        })
      }
    } catch { /* no user stored */ }
  }, [])

  return (
    <ThemeProvider>
      <BrowserRouter>
        <RouteTracker />
        <ErrorBoundary>
        <Routes>
          {/* Public Homepage */}
          <Route
            path="/home"
            element={<HomePage />}
          />

          {/* New homepage concept — setlist pages for DJs */}
          <Route
            path="/test"
            element={<TestPage />}
          />

          {/* Investor Deck */}
          <Route path="/deck" element={<DeckPage />} />
          {/* Investor Deck v2 — simple, emotional + mocks */}
          <Route path="/deck2" element={<Deck2Page />} />
          {/* Investor Deck v3 — refined (conversation changes, gray boxes, text left + imagery right) */}
          <Route path="/deck3" element={<Deck3Page />} />
          {/* Investor Deck Final — copy of deck3 for post-call edits */}
          <Route path="/deck-final" element={<DeckFinalPage />} />
          {/* Investor Deck Final (cool images variant) */}
          <Route path="/cool-deck-final" element={<DeckFinalPage />} />
          {/* SAFE Deck — cool deck imagery + SAFE scenarios slide in place of raise slide */}
          <Route path="/safe-deck" element={<SafeDeckPage />} />
          {/* Simple Deck — slimmed-down version of cool-deck-final */}
          <Route path="/simple-deck" element={<SyncfulDeckPage />} />
          {/* Strategic Advisory Group */}
          <Route path="/strategic-advisory-group" element={<StrategicAdvisoryGroupPage />} />
          {/* Investor Deck v4 — simplified version of deck3 */}
          <Route path="/deck4" element={<Deck4Page />} />
          {/* Product Deck */}
          <Route path="/product-deck" element={<ProductDeckPage />} />
          <Route path="/deck-multi-industry" element={<MultiIndustryDeckPage />} />
          <Route path="/deck-cross-reference-data" element={<CrossReferenceDataDeckPage />} />
          {/* Team handbook (same password as most decks) */}
          <Route path="/team-handbook" element={<TeamHandbookPage />} />
          {/* Master Plan (password-protected) */}
          <Route path="/plan" element={<MasterPlanPage />} />
          {/* Plan Richard — Phase 1 & 2 deliverables (same password) */}
          <Route path="/plan-richard" element={<PlanRichardPage />} />
          {/* Rollout & customer journey (same password); SIDEPIECE + Ryan highlighted */}
          <Route path="/plan-rollout" element={<PlanRolloutPage />} />
          {/* April development plan — codebase-audited (same password) */}
          <Route path="/plan-april" element={<PlanAprilPage />} />
          <Route path="/plan-march" element={<Navigate to="/plan-april" replace />} />
          {/* Action plan — GTM, product, API, fundraising (password set in ActionPlanPage) */}
          <Route path="/action-plan" element={<ActionPlanPage />} />
          {/* Founders trip agenda — April 2026 */}
          <Route path="/agenda" element={<AgendaPage />} />

          {/* Wrapped — Year-in-review Spotify-Wrapped-style report (password-protected) */}
          <Route path="/wrapped" element={<WrappedPage />} />

          {/* Intelligence — full-screen, no sidebar */}
          <Route
            path="/intelligence"
            element={
              <RequireProtect>
                <RequireAuth>
                  <Suspense fallback={<PageSkeleton />}>
                    <IntelligencePage />
                  </Suspense>
                </RequireAuth>
              </RequireProtect>
            }
          />

          {/* Daily Digest — full-screen, no sidebar */}
          <Route
            path="/digest"
            element={
              <RequireProtect>
                <RequireAuth>
                  {/* Plain backdrop while the digest chunk loads — DigestPage owns the orb + "Get ready" so we don’t mount two OrangeOrb3D sequences in a row. */}
                  <Suspense
                    fallback={
                      <div
                        className="fixed inset-0 z-[100] bg-[#0A0A0A]"
                        aria-hidden
                      />
                    }
                  >
                    <DigestPage />
                  </Suspense>
                </RequireAuth>
              </RequireProtect>
            }
          />

          {/* Admin Console — separate auth, no regular sidebar */}
          <Route path="/admin" element={<AdminPage />} />

          {/* Developer API — keys, pricing, link to Swagger */}
          <Route path="/developers" element={<DeveloperPortalPage />} />
          <Route path="/docs" element={<ApiDocsPage />} />

          {/* Public Legal Pages */}
          <Route
            path="/privacy-policy"
            element={
              <PageTransition>
                <PrivacyPolicyPage />
              </PageTransition>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <PageTransition>
                <TermsOfServicePage />
              </PageTransition>
            }
          />
          <Route
            path="/accessibility-statement"
            element={
              <PageTransition>
                <AccessibilityStatementPage />
              </PageTransition>
            }
          />
          <Route
            path="/whitepaper"
            element={
              <PageTransition>
                <WhitepaperPage />
              </PageTransition>
            }
          />
          {/* Public Dizee Link pages (no auth) */}
          <Route path="/l/collect/:linkId" element={<Suspense fallback={<PageSkeleton />}><CollectLinkPage /></Suspense>} />
          <Route path="/l/release/:slug" element={<Suspense fallback={<PageSkeleton />}><ReleaseLinkPage /></Suspense>} />
          <Route path="/l/guestlist/:slug" element={<Suspense fallback={<PageSkeleton />}><GuestListPage /></Suspense>} />
          <Route path="/guest/:guestSlug/:showSegment" element={<Suspense fallback={<PageSkeleton />}><GuestListPage /></Suspense>} />
          <Route path="/guest/:guestSlug" element={<Suspense fallback={<PageSkeleton />}><GuestListPage /></Suspense>} />
          <Route path="/tickets/:slug/:showSegment" element={<Suspense fallback={<PageSkeleton />}><TicketsPage /></Suspense>} />
          <Route path="/tickets/:slug" element={<Suspense fallback={<PageSkeleton />}><TicketsPage /></Suspense>} />

          {/* Auth Pages */}
          <Route
            path="/protect"
            element={
              <PageTransition>
                <ProtectPage />
              </PageTransition>
            }
          />
          <Route
            path="/login"
            element={
              <RequireProtect>
                <PageTransition>
                  <LoginPage />
                </PageTransition>
              </RequireProtect>
            }
          />
          <Route
            path="/login-otp"
            element={
              <RequireProtect>
                <PageTransition>
                  <LoginOtpPage />
                </PageTransition>
              </RequireProtect>
            }
          />
          <Route
            path="/signup"
            element={
              <RequireProtect>
                <PageTransition>
                  <SignupPage />
                </PageTransition>
              </RequireProtect>
            }
          />

          {/* Authenticated App — Sidebar Layout */}
          <Route
            path="/"
            element={
              <RequireProtect>
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              </RequireProtect>
            }
          >
            <Route index element={<SmartRedirect />} />
            <Route path="overview" element={<ProtectedRoute requiredPermission="overview"><Suspense fallback={<PageSkeleton />}><OverviewPage /></Suspense></ProtectedRoute>} />
            <Route path="dashboard" element={<ProtectedRoute requiredPermission="activity"><DashboardOverviewPage /></ProtectedRoute>} />
            <Route path="team-hub" element={<Navigate to="/team" replace />} />
            <Route path="promoter" element={<Navigate to="/shows" replace />} />
            <Route path="manager-dashboard" element={<Navigate to="/shows" replace />} />
            <Route path="support-dashboard" element={<Navigate to="/shows" replace />} />
            <Route path="chat" element={<ProtectedRoute requiredPermission="chat"><Suspense fallback={<PageSkeleton />}><ChatPage /></Suspense></ProtectedRoute>} />
            <Route
              path="agents/setup"
              element={
                <ProtectedRoute requiredPermission="chat">
                  <Suspense fallback={<PageSkeleton />}>
                    <AgentSetupPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="agents/:specialistId"
              element={
                <ProtectedRoute requiredPermission="chat">
                  <Suspense fallback={<PageSkeleton />}>
                    <AgentChatPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="schedule" element={<ProtectedRoute requiredPermission="schedule"><Suspense fallback={<PageSkeleton />}><SchedulePage /></Suspense></ProtectedRoute>} />
            <Route path="shows" element={<ProtectedRoute requiredPermission="shows"><Suspense fallback={<PageSkeleton />}><ShowsPage /></Suspense></ProtectedRoute>} />
            <Route path="tour-manager/show/:showId" element={<Navigate to="/tour-manager" replace />} />
            <Route
              path="tour-manager"
              element={
                <ProtectedRoute requiredPermission="shows">
                  <Suspense fallback={<PageSkeleton />}>
                    <TourManagerPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="tasks" element={<ProtectedRoute requiredPermission="tasks"><Suspense fallback={<PageSkeleton />}><TasksPage /></Suspense></ProtectedRoute>} />
            <Route path="knowledge" element={<ProtectedRoute requiredPermission="knowledge"><Suspense fallback={<PageSkeleton />}><KnowledgePage /></Suspense></ProtectedRoute>} />
            <Route path="music" element={<ProtectedRoute requiredPermission="music"><Suspense fallback={<PageSkeleton />}><MusicPage /></Suspense></ProtectedRoute>} />
            <Route path="links" element={<ProtectedRoute requiredPermission="links"><Suspense fallback={<PageSkeleton />}><LinksPage /></Suspense></ProtectedRoute>} />
            <Route
              path="people"
              element={
                <ProtectedRoute requiredPermission="links">
                  <Suspense fallback={<PageSkeleton />}>
                    <PeoplePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="links/guestlist"
              element={
                <ProtectedRoute requiredPermission="links">
                  <Navigate to="/links" replace state={{ openGuestList: true }} />
                </ProtectedRoute>
              }
            />
            <Route
              path="links/tickets"
              element={
                <ProtectedRoute requiredPermission="links">
                  <Navigate to="/links" replace state={{ openTickets: true }} />
                </ProtectedRoute>
              }
            />
            <Route
              path="links/:linkId"
              element={
                <ProtectedRoute requiredPermission="links">
                  <LinksManageRedirect />
                </ProtectedRoute>
              }
            />
            <Route path="intelligence-dashboard" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><IntelligenceDashboardPage /></Suspense></ProtectedRoute>} />
            <Route path="team" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><TeamPage /></Suspense></ProtectedRoute>} />
            <Route path="team/:teamMemberId/edit" element={<ProtectedRoute requiredPermission="team"><Suspense fallback={<PageSkeleton />}><TeamMemberEditPage /></Suspense></ProtectedRoute>} />
            <Route path="activity" element={<ProtectedRoute requiredPermission="activity"><Suspense fallback={<PageSkeleton />}><ActivityPage /></Suspense></ProtectedRoute>} />
            <Route
              path="email"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}>
                    <EmailPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="money" element={<ProtectedRoute requiredPermission="finance"><Suspense fallback={<PageSkeleton />}><MoneyPage /></Suspense></ProtectedRoute>} />
            <Route path="deals" element={<ProtectedRoute requiredPermission="deals"><Suspense fallback={<PageSkeleton />}><DealsPage /></Suspense></ProtectedRoute>} />
            <Route path="deals/:dealId" element={<ProtectedRoute requiredPermission="deals"><Suspense fallback={<PageSkeleton />}><DealDetailPage /></Suspense></ProtectedRoute>} />
            <Route path="brand-deals" element={<Navigate to="/deals" replace />} />
            <Route path="money-test" element={<Suspense fallback={<PageSkeleton />}><MoneyTestPage /></Suspense>} />
            <Route path="finance" element={<Navigate to="/money" replace />} />
            <Route path="settings" element={<ProtectedRoute requiredPermission="settings"><Suspense fallback={<PageSkeleton />}><SettingsPage /></Suspense></ProtectedRoute>} />
            <Route path="google-calendar-callback" element={<ProtectedRoute requiredPermission="schedule"><Suspense fallback={<PageSkeleton />}><GoogleCalendarCallbackPage /></Suspense></ProtectedRoute>} />
          </Route>
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
